import { React, useState, useEffect, useRef } from 'react';
import { Logout } from '../auth/Auth';
import apiClient from '../auth/authenticatedClient';
import { TOAST_TIMEOUT } from '../constants';
import { Calendar } from './HabitCalendar';
import { ContributionHeatmap } from './ContributionHeatmap';
import {
  IconButton,
  Divider,
  VStack,
  Center,
  Flex,
  Box,
  useBreakpointValue,
  Card,
  Heading,
  CardHeader, CardBody, CardFooter,
  Editable, EditablePreview, EditableInput,
  Text,
  Skeleton,
  Input,
  useToast,
  HStack,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import ProtectedHeader from '../ProtectedHeader';
import ProtectedFooter from '../ProtectedFooter';
import cronstrue from 'cronstrue'; // Import the cronstrue library
import { DeleteIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { errorToast } from '../error/errorToast';
import { computeStreak } from './streaks';
import { StreakBadge } from './StreakBadge';
import { celebrateCheckIn } from './rewards';

/*
 * These are hardcoded messages for underlying cron expressions, 
 * even if they are not actually accurate to cron expressions.
 * This is mainly for general habits which are only supported on very fixed schedules.
 */
const cronMapping = {
  '0 0 * * *': 'Every day',
  '0 0 * * 0': 'Every Week',
  // Add more mappings here
};

function HabitCard() {
  const formPadding = useBreakpointValue({ base: '5', md: '8' });
  const [habit, setHabit] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1)
  const [values, setValues] = useState([today, tomorrow])
  const navigate = useNavigate();
  // Guards against double-taps on a calendar cell while its toggle is in
  // flight, without blocking taps on other days.
  const pendingDays = useRef(new Set());
  // The day whose cell plays the check-burst animation; cleared shortly
  // after so the cell returns to showing its day number.
  const [burstDay, setBurstDay] = useState(null);
  const burstTimer = useRef(null);
  

  const fetchSingleHabit = async (habitId) => {
    const response = await apiClient.get(`/habit/${habitId}`);
    return response.data;
  };

  useEffect(() => {
    const habitId = window.location.pathname.split('/').pop();
    fetchSingleHabit(habitId)
      .then((data) => {
        setHabit(data);
        setIsLoading(false);
      })
      .catch((error) => {
        toast({
          title: 'Error',
          description: error.message,
          status: 'error',
          duration: TOAST_TIMEOUT,
          isClosable: true,
        });
        navigate('/habits')
      });
  }, []);

  const getReadableFrequency = (frequency) => {
    try {
      return cronstrue.toString(frequency); // Convert cron expression to human-readable text
    } catch (error) {
      console.error('Failed to convert cron expression:', error);
      return frequency; // Return the original frequency if conversion fails
    }
  };
  const getReadableFrequencyMessage = (frequency) => {
    return cronMapping[frequency] || getReadableFrequency(frequency);
  };

  const getStreakMessage = (habitLogs) => {
    const streak = computeStreak(habitLogs);
    if (streak.count === 0) {
      return 'Check in today to start one';
    }
    const days = `${streak.count} day${streak.count === 1 ? '' : 's'}`;
    if (streak.status === 'repairable') {
      return `${days} — paused. Check in yesterday to keep it going`;
    }
    return streak.doneToday ? `${days} 🔥` : `${days} — check in today to keep it going`;
  };
  
  const handleDeleteButton = async () => {
    const habitId = window.location.pathname.split('/').pop();
    try {
      await apiClient.delete(`/habit/${habitId}`);
      navigate('/habits');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: TOAST_TIMEOUT,
        isClosable: true,
      });
    }
  }

            

  const handlePromptSubmit = async (prompt) => {
    const habitId = window.location.pathname.split('/').pop();
    try {
      await apiClient.patch(`/habit/${habitId}`, { prompt });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: TOAST_TIMEOUT,
        isClosable: true,
      });
    }
  }

  const handleGoBack = () => {
    navigate(-1); // Go back to the previous page
  };

  const patchHabitLogs = (updater) =>
    setHabit((h) => ({ ...h, habitLogs: updater(h.habitLogs) }));

  // Tap a calendar day: log a check-in for it, or remove the existing one.
  // Optimistic in both directions — the cell flips immediately and rolls back
  // with an error toast if the request fails (same pattern as Habits.js).
  const handleToggleDay = (date) => {
    const dayKey = new Date(date).toDateString();
    if (pendingDays.current.has(dayKey)) return;
    pendingDays.current.add(dayKey);
    const habitId = window.location.pathname.split('/').pop();

    const existing = habit.habitLogs.find(
      (log) => new Date(log.recordTs).toDateString() === new Date(date).toDateString()
    );

    if (existing) {
      patchHabitLogs((logs) => logs.filter((log) => log.id !== existing.id));
      apiClient.delete(`/habit/${habitId}/entry/${existing.id}`)
        .catch((error) => {
          patchHabitLogs((logs) => [...logs, existing]);
          errorToast(toast, error.toString());
        })
        .finally(() => pendingDays.current.delete(dayKey));
      return;
    }

    const tempLog = { id: `pending-${date.toISOString()}`, recordTs: new Date(date).toISOString() };
    patchHabitLogs((logs) => [...logs, tempLog]);
    setBurstDay(date);
    clearTimeout(burstTimer.current);
    // Long enough for the burst (ring + sparkles + check pop) to finish.
    burstTimer.current = setTimeout(() => setBurstDay(null), 900);
    celebrateCheckIn({ toast, prompt: habit.prompt, logs: [...habit.habitLogs, tempLog], date });
    apiClient.post(`/habit/${habitId}/entry`, { date: date.toISOString() })
      .then((response) => {
        patchHabitLogs((logs) =>
          logs.map((log) => (log.id === tempLog.id ? { ...tempLog, ...response.data } : log))
        );
      })
      .catch((error) => {
        patchHabitLogs((logs) => logs.filter((log) => log.id !== tempLog.id));
        errorToast(toast, error.toString());
      })
      .finally(() => pendingDays.current.delete(dayKey));
  };

  return (
    <Flex direction="column" h="100vh">
      <ProtectedHeader />
      <Center flex="1" alignItems="flex-start">
        <Box
          w={{ base: '90%', md: '800px' }}
          p={formPadding}
          rounded="lg"
        >
          {isLoading ? (
            <Skeleton height="20px" />
          ) : (
            <Card>
              <CardHeader>
            <HStack>
                <IconButton
                  size='md'
                  variant='solid'
                  icon={<ChevronLeftIcon />}
                  onClick={handleGoBack} // Add onClick event to go back
                ></IconButton>
                <Editable defaultValue={habit.prompt}
                  onSubmit={handlePromptSubmit}
                  fontSize='2xl'
                  color='teal'
                >
                  <EditablePreview />
                  <EditableInput />
                </Editable>
                <StreakBadge streak={computeStreak(habit.habitLogs)} />
</HStack>
              </CardHeader>
              <Box position='relative' padding={1}>
                <Divider />
              </Box>
              <CardBody>
                <VStack align='left' spacing={4}>
                  <HStack>
                    <Text color='darkcyan'>Frequency: </Text><Text>{getReadableFrequencyMessage(habit.frequency)}</Text> {/* Display the human-readable frequency */}
                  </HStack>
                  <HStack>
                    <Text color='darkcyan'>Streak: </Text><Text>{getStreakMessage(habit.habitLogs)}</Text>
                  </HStack>
                  <Calendar
                    habitLogs={habit.habitLogs}
                    onToggleDay={handleToggleDay}
                    burstDay={burstDay}
                    createdTs={habit.createdTs}
                  />
                  <Divider />
                  <ContributionHeatmap habitLogs={habit.habitLogs} />
                  <Button colorScheme="red" variant="solid" size="md" onClick={handleDeleteButton}>
                    <DeleteIcon mr={2} />
                    Delete
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>
      </Center>
      <ProtectedFooter />
    </Flex>
  );
}

export { HabitCard };